import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../dashboard/DashboardIcons';
import PremiumDatePicker from './PremiumDatePicker';
import { PERIOD_PRESETS, resolvePreset } from '../../lib/dashboardPeriods';

const PANEL_W = 264;
const CUSTOM_PANEL_W = 560;
const MOBILE_BREAKPOINT = 640;

/** زر فترة زمنية بلوحة اختصارات جاهزة + تحديد فترة مخصصة — يستبدل قائمة الأشهر البسيطة
 *  اللوحة تُعرض عبر Portal بموضع ثابت لتفادي القص من أي عنصر أب بـoverflow-hidden
 *  عند "تحديد فترة مخصصة" يظهر تقويم واحد بجانب قائمة الاختصارات (لا يظهر تحتها) لتجنّب التمرير */
export default function PeriodRangePicker({ value, onChange, theme = 'light', testId = 'period-range-picker' }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.preset === 'custom' ? value.startDate : '');
  const [customTo, setCustomTo] = useState(value?.preset === 'custom' ? value.endDate : '');
  const [showCustom, setShowCustom] = useState(value?.preset === 'custom');
  const [activeField, setActiveField] = useState('from');
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const computePos = useCallback((custom) => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const desiredWidth = custom ? (isMobile ? 320 : CUSTOM_PANEL_W) : PANEL_W;
    const panelWidth = Math.min(desiredWidth, window.innerWidth - 16);
    let left = rect.right - panelWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
    const top = rect.bottom + 8;
    const maxHeight = Math.max(240, window.innerHeight - top - 16);
    setPos({ top, left, width: panelWidth, maxHeight, isMobile });
  }, []);

  useLayoutEffect(() => {
    if (open) computePos(showCustom);
  }, [open, showCustom, computePos]);

  const closeOnOutside = useCallback((e) => {
    if (
      triggerRef.current && !triggerRef.current.contains(e.target) &&
      panelRef.current && !panelRef.current.contains(e.target)
    ) setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', closeOnOutside);
    const onResize = () => computePos(showCustom);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('resize', onResize);
    };
  }, [open, closeOnOutside, computePos, showCustom]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  const pickPreset = (key) => {
    if (key === 'custom') { setShowCustom(true); setActiveField('from'); return; }
    setShowCustom(false);
    onChange(resolvePreset(key));
    setOpen(false);
  };

  const pickCustomDay = (iso) => {
    if (activeField === 'from') {
      setCustomFrom(iso);
      if (!customTo || iso > customTo) setCustomTo('');
      setActiveField('to');
    } else {
      setCustomTo(iso);
    }
  };

  const applyCustom = () => {
    if (!customFrom) return;
    onChange(resolvePreset('custom', { startDate: customFrom, endDate: customTo || customFrom }));
    setOpen(false);
  };

  const dark = theme === 'dark';
  const mutedText = dark ? 'text-slate-300' : 'text-slate-500';
  const calendarValue = activeField === 'from' ? customFrom : customTo;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${dark ? 'border-white/15 bg-[#1c2a44] text-white hover:border-teal-400/50' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-teal-300'}`}
      >
        <Icon name="chevronDown" className="h-3.5 w-3.5 shrink-0" />
        <span data-testid={`${testId}-label`} dir={value?.preset === 'custom' ? 'ltr' : undefined}>{value?.label || 'الفترة المعروضة'}</span>
        <Icon name="calendar" className={`h-4 w-4 shrink-0 ${dark ? 'text-teal-300' : 'text-teal-600'}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, zIndex: 9999, overscrollBehavior: 'contain' }}
          className={`fixed flex flex-col overflow-y-auto rounded-xl border shadow-2xl sm:flex-row sm:overflow-hidden ${dark ? 'border-white/10 bg-[#16233b]' : 'border-slate-200 bg-white'}`}
          data-testid={`${testId}-panel`}
        >
          <div className={`max-h-[14rem] w-full shrink-0 overflow-y-auto p-1.5 sm:max-h-[22rem] sm:w-[190px] ${showCustom ? (dark ? 'border-b sm:border-b-0 sm:border-l border-white/10' : 'border-b sm:border-b-0 sm:border-l border-slate-100') : ''}`} style={{ overscrollBehavior: 'contain' }}>
            {PERIOD_PRESETS.map((p) => {
              const isActive = value?.preset === p.key || (p.key === 'custom' && showCustom);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pickPreset(p.key)}
                  data-testid={`${testId}-option-${p.key}`}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-teal-500/15 text-teal-600 ring-1 ring-teal-400'
                      : dark ? 'text-slate-200 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p.label}
                  {isActive && <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />}
                </button>
              );
            })}
          </div>

          {showCustom && (
            <div className="w-full shrink-0 p-3 sm:w-[370px]" data-testid={`${testId}-custom-panel`}>
              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveField('from')}
                  data-testid={`${testId}-tab-from`}
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-right text-xs transition-colors ${activeField === 'from' ? 'border-teal-400 bg-teal-500/10 text-teal-600' : `border-slate-200 ${mutedText}`}`}
                >
                  <span className="block font-semibold">من تاريخ</span>
                  <span className="block font-bold">{customFrom || 'اختر تاريخاً'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveField('to')}
                  data-testid={`${testId}-tab-to`}
                  className={`flex-1 rounded-lg border px-2 py-1.5 text-right text-xs transition-colors ${activeField === 'to' ? 'border-teal-400 bg-teal-500/10 text-teal-600' : `border-slate-200 ${mutedText}`}`}
                >
                  <span className="block font-semibold">إلى تاريخ</span>
                  <span className="block font-bold">{customTo || 'اختر تاريخاً'}</span>
                </button>
              </div>

              <PremiumDatePicker
                inline
                value={calendarValue}
                onChange={pickCustomDay}
                testId={`${testId}-custom-cal`}
                allowClear={false}
                minDate={activeField === 'to' ? customFrom || undefined : undefined}
              />

              <button
                type="button"
                onClick={applyCustom}
                disabled={!customFrom}
                data-testid={`${testId}-custom-apply`}
                className="mt-2 w-full rounded-lg bg-teal-500 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                تطبيق الفترة
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
